/**
 * Shared card FACE renderer — the rarity dot + cost + name + basic STATS that make a
 * card read like a playing card. Used by both the in-match hand ({@link ./hand.ts}) and
 * the pre-match deck builder ({@link ./deckBuilder.ts}), so a card looks the same everywhere.
 *
 * Offense (a {@link Club}): four 1–5 reliability bars (PWR / ACC / CTL / FRG) + a flight-trait
 * line (spin · flight · shape). Defense (a {@link DefenseCard}): the effect it plays.
 * Pure DOM building — no sim mutation.
 */
import type { Club, DefenseCard } from '../sim/types.ts';

/** Is this an offensive card (a Club)? */
function isClub(card: Club | DefenseCard): card is Club {
  return 'power' in card;
}

const OFFENSE_STATS: readonly (readonly [keyof Club, string])[] = [
  ['power', 'PWR'],
  ['accuracy', 'ACC'],
  ['control', 'CTL'],
  ['forgiveness', 'FRG'],
];

const SPIN_LABEL: Record<Club['spin'], string> = { stop: 'STOP', mid: 'CHK', run: 'RUN' };
const FLIGHT_LABEL: Record<Club['flight'], string> = { high: 'HIGH', mid: 'MID', low: 'LOW' };
const SHAPE_LABEL: Record<Club['shape'], string> = { straight: 'STR', fade: 'FADE', draw: 'DRAW' };

/** One word for what a defense card does (the card's "position" / type). */
function defenseEffect(card: DefenseCard): string {
  if (card.hazard === 'water') return 'WATER';
  if (card.hazard === 'sand') return 'BUNKER';
  if (card.hazard === 'tree') return 'TREE';
  if (card.special === 'rough') return 'ROUGH';
  if (card.special === 'wind') return 'WIND';
  if (card.special === 'gopher') return 'STEAL';
  return '';
}

/** The three defensive rating bars (1–5) + a flavour tag per card, so a defense card reads like a
 *  sports card the same way an offensive club does — THReat / Area-of-effect / DURation. */
const DEFENSE_BAR_LABELS = ['THR', 'AOE', 'DUR'] as const;
const DEFENSE_PROFILES: Record<string, { bars: readonly [number, number, number]; tag: string }> = {
  bunker: { bars: [3, 2, 4], tag: 'Sandy +2' },
  pond: { bars: [4, 3, 4], tag: 'Splash +2' },
  moat: { bars: [5, 5, 4], tag: 'Wide carry' },
  rough: { bars: [2, 3, 4], tag: 'Fouls a tee' },
  wind: { bars: [2, 5, 2], tag: 'Blows shots' },
  timber: { bars: [3, 2, 4], tag: 'Blocks low' },
  gopher: { bars: [5, 1, 1], tag: 'Double par' },
};
const DEFENSE_FALLBACK = { bars: [3, 3, 3] as const, tag: '' };

/** A labelled 1–5 rating bar row (shared by offense + defense so both read identically). */
function statRow(label: string, value: number): HTMLElement {
  const row = document.createElement('span');
  row.className = 'fc-stat';
  const lab = document.createElement('b');
  lab.textContent = label;
  const bar = document.createElement('i');
  bar.className = 'fc-stat__bar';
  const fill = document.createElement('i');
  fill.className = 'fc-stat__fill';
  fill.style.width = `${(Math.max(0, Math.min(5, value)) / 5) * 100}%`;
  bar.appendChild(fill);
  row.append(lab, bar);
  return row;
}

/** A small bottom "traits" line (the club's flight character, or the defense's type + tag). */
function traitsLine(text: string): HTMLElement {
  const traits = document.createElement('span');
  traits.className = 'fc-card__traits';
  traits.textContent = text;
  return traits;
}

/** Build the stat block for a card — rating bars + a traits line — so offense AND defense both
 *  read like sports cards (offense: PWR/ACC/CTL/FRG; defense: THR/AOE/DUR). */
export function buildCardStats(card: Club | DefenseCard): HTMLElement {
  const stats = document.createElement('div');
  stats.className = 'fc-card__stats';

  if (isClub(card)) {
    for (const [key, label] of OFFENSE_STATS) stats.appendChild(statRow(label, card[key] as number));
    stats.appendChild(
      traitsLine(`${SPIN_LABEL[card.spin]} · ${FLIGHT_LABEL[card.flight]} · ${SHAPE_LABEL[card.shape]}`),
    );
  } else {
    const prof = DEFENSE_PROFILES[card.id] ?? DEFENSE_FALLBACK;
    DEFENSE_BAR_LABELS.forEach((label, i) => stats.appendChild(statRow(label, prof.bars[i] ?? 0)));
    stats.appendChild(traitsLine(`${defenseEffect(card)} · ${prof.tag}`));
  }

  return stats;
}

/**
 * Populate `el` with a card's full face: rarity dot, cost badge, name, and the stats block.
 * `el` should already carry the `fc-card` (+ variant) classes from the caller.
 */
export function fillCardFace(el: HTMLElement, card: Club | DefenseCard): void {
  const rar = document.createElement('span');
  rar.className = `fc-card__rar fc-card__rar--${card.rarity}`;

  const cost = document.createElement('span');
  cost.className = 'fc-card__cost';
  cost.textContent = String(card.cost);

  const name = document.createElement('span');
  name.className = 'fc-card__name';
  name.textContent = card.name;

  el.append(rar, cost, name, buildCardStats(card));
}
